import { Cta } from "@/components/Cta";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { useSeo } from "@/lib/seo";
import { Card, Container, Grid, Stack, Text, Title } from "@repo/ui/core";
import {
  IconArrowRight,
  IconCheck,
  IconCurrencyDollar,
  IconRocket,
  IconUsers,
} from "@repo/ui/icons";
import styles from "@styles/site.module.css";

const pillars = [
  {
    title: "Predictable Costs",
    icon: IconCurrencyDollar,
    description:
      "Usage-based billing through your existing cloud accounts. Burst compute exists only while your pipeline runs, so spend tracks actual build activity.",
    bullets: ["Pass-through AWS/GCP billing", "Auto-scaling infrastructure", "Compute exists only while pipelines run"],
  },
  {
    title: "Faster Time to Market",
    icon: IconRocket,
    description:
      "Snapshot-accelerated builds mean more iterations per day. Cloud emulators eliminate hardware bottlenecks. Ship features, not infrastructure.",
    bullets: ["Intelligent build caching", "Parallel testing at scale", "Automated deployment"],
  },
  {
    title: "Global Team Enablement",
    icon: IconUsers,
    description:
      "Onboard developers and contractors in minutes from anywhere. No laptops to configure or ship. Secure access through GitLab.",
    bullets: ["Browser-based development", "Multi-region infrastructure", "Enterprise security"],
  },
];

const metrics = [
  { value: "< 10 min", label: "Full AAOSP pipeline build" },
  { value: "2", label: "Regions live today, US and EU" },
  { value: "40+", label: "Languages for behavioral tests" },
  { value: "Minutes", label: "To onboard a developer or contractor" },
];

export default function EngineeringLeaders() {
  useSeo("/solutions/engineering-leaders");

  return (
    <>
      <PageHero
        eyebrow="For Engineering Leaders"
        title={
          <>
            Ship faster. Spend less.{" "}
            <span className={styles.gradientText}>Scale globally.</span>
          </>
        }
        description="Idle build servers, bench hardware, and weeks of contractor onboarding are where automotive programs lose quarters. Null Autos removes all three."
      >
        <Cta to="/contact">Book a Demo</Cta>
        <Cta to="/platform" variant="ghost" iconRight={<IconArrowRight size={16} />}>
          Explore Platform
        </Cta>
      </PageHero>

      {/* Pillars */}
      <Section>
        <SectionHeader
          title="Built for engineering leadership"
          description="Cost, velocity, and hiring: the three levers this platform moves."
        />
        <Grid gutter="lg">
          {pillars.map((pillar, i) => (
            <Grid.Col key={pillar.title} span={{ base: 12, md: 4 }}>
              <Reveal delay={i * 0.05} className={undefined}>
                <Card padding="xl" className={styles.card} style={{ height: "100%" }}>
                  <Stack gap="md">
                    <span className={styles.iconChip}>
                      <pillar.icon size={20} stroke={1.5} />
                    </span>
                    <Title order={3} size="1.25rem" fw={600}>
                      {pillar.title}
                    </Title>
                    <Text c="dimmed" size="sm" lh={1.7}>
                      {pillar.description}
                    </Text>
                    <ul className={styles.checkList} style={{ marginTop: 8 }}>
                      {pillar.bullets.map((bullet) => (
                        <li key={bullet}>
                          <IconCheck size={16} stroke={2.5} />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </Stack>
                </Card>
              </Reveal>
            </Grid.Col>
          ))}
        </Grid>
      </Section>

      {/* Evidence strip */}
      <Section tight hairline>
        <div className={styles.metricGrid}>
          {metrics.map((metric, i) => (
            <Reveal key={metric.label} delay={i * 0.05}>
              <div className={styles.metric}>
                <div className={styles.metricValue}>{metric.value}</div>
                <div className={styles.metricLabel}>{metric.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section className={styles.finalCta}>
        <Container size="xl" py={88} ta="center">
          <Reveal>
            <Title order={2} fw={650} size="2rem" style={{ letterSpacing: "-0.02em" }}>
              See the cost model on your own numbers
            </Title>
            <Text size="lg" c="dimmed" maw={480} mx="auto" mt="md" mb="xl" lh={1.65}>
              Bring your current build times and hardware spend. We will map the delta.
            </Text>
            <div className={styles.ctaRow} style={{ justifyContent: "center" }}>
              <Cta to="/contact">Book a Demo</Cta>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
